import Header from "../../components/header";
import LeftMenu from "../../components/left-menu";
import NewMailDialog from "../../components/NewMailDialog";
import Grid from "@mui/material/Grid";
import "./style.css";

import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";

import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableBody from "@mui/material/TableBody";
import Dialog from "@mui/material/Dialog";
import LinearProgress from "@mui/material/LinearProgress";
import Box from "@mui/material/Box";
import Alert from "@mui/material/Alert";
import Chip from "@mui/material/Chip";

import { Select, MenuItem, Typography } from "@mui/material";
import { purple } from "@mui/material/colors";
import { styled } from "@mui/material/styles";
import {
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
} from "@mui/material";

const LIMIT = 10;

const ColorButton = styled(Button)(({ theme }) => ({
  color: theme.palette.getContrastText(purple[500]),
  backgroundColor: purple[500],
  "&:hover": {
    backgroundColor: purple[700],
  },
}));

function normalizeResponseToArray(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.results)) return data.results;
  return [];
}

function getContractList(post) {
  if (Array.isArray(post?.kontrak)) return post.kontrak;
  if (Array.isArray(post?.kontrakList)) return post.kontrakList;
  if (post?.kontrak) return [post.kontrak];
  return [];
}

function getMainContract(post) {
  const list = getContractList(post);
  return list[0] || null;
}

function getCompanyName(post) {
  const contract = getMainContract(post);

  return (
    post?.company?.namaPerusahaan ||
    post?.company?.NamaPerusahaan ||
    contract?.CompanyName ||
    contract?.NamaPT ||
    ""
  );
}

function getClientName(post) {
  const contract = getMainContract(post);

  return (
    post?.klien?.Nama ||
    post?.klien?.nama ||
    contract?.KlienNama ||
    contract?.NamaKlien ||
    ""
  );
}

function getClientPhone(post) {
  const phone = post?.klien?.Phone;

  if (Array.isArray(phone)) return phone[0] || "";
  return phone || "";
}

function getContractStatus(startDate, endDate, terminateStat) {
  if (terminateStat === "Yes") return "Expired";

  if (!startDate || !endDate) return "-";

  const currentDate = new Date();
  const starting = new Date(startDate);
  const ending = new Date(endDate);

  if (Number.isNaN(starting.getTime()) || Number.isNaN(ending.getTime())) {
    return "-";
  }

  if (currentDate < starting) return "Belum Mulai";
  if (currentDate >= starting && currentDate <= ending) return "Aktif";
  return "Expired";
}

function getStatusColor(status) {
  if (status === "Aktif") return "success";
  if (status === "Belum Mulai") return "warning";
  if (status === "Expired") return "error";
  return "default";
}

function getRecipientBlockReason(post) {
  const contract = getMainContract(post);
  const companyName = getCompanyName(post);
  const clientName = getClientName(post);
  const clientPhone = getClientPhone(post);

  if (post?.recipientReady === false) {
    return (
      post?.missingReason ||
      "Data klien / nomor WhatsApp belum lengkap. Tidak bisa kirim notifikasi."
    );
  }

  if (!companyName) {
    return "Nama perusahaan tidak ditemukan. Periksa data company / kontrak.";
  }

  /**
   * Allow open when KlienID exists in kontrak even if current old list API failed
   * to hydrate `post.klien`. The fixed backend can still resolve it from kontrak.KlienID.
   */
  if (!clientName && !contract?.KlienID && !post?.klien?._id) {
    return "Nama klien / KlienID tidak ditemukan. Periksa data kontrak.";
  }

  /**
   * If a klien object exists but has no phone, block immediately.
   * This prevents admin submitting a row that definitely cannot be sent.
   */
  if (post?.klien?._id && !clientPhone) {
    return `Nomor WhatsApp klien kosong: ${clientName || post?.klien?._id}`;
  }

  return "";
}

export default function CreatePost() {
  const [skip, setSkip] = useState(0);
  const [posts, setPosts] = useState([]);
  const [isEnd, setIsEnd] = useState("Loading...");
  const [query, setQuery] = useState("");

  const [categorias, setCategorias] = useState([]);
  const [address1, setAddress1] = useState("semua");

  const [searchThis, setSearchThis] = useState("Perusahaan");
  const [statusnya, setStatusnya] = useState("Aktif");
  const [khusus, setKhusus] = useState("");

  const [loadingList, setLoadingList] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [pageError, setPageError] = useState("");

  const [useData, setUseData] = useState(null);
  const [openNewMail, setOpenNewMail] = useState(false);

  const requestIdRef = useRef(0);

  const getCategorias = useCallback(async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/addressOptions`
      );

      setCategorias(response.data?.pilihanAlamat || []);
    } catch (error) {
      console.error(error);
      setPageError(
        error.response?.data?.message ||
          error.message ||
          "Gagal mengambil pilihan lokasi."
      );
    }
  }, []);

  const fetchPosts = useCallback(
    async ({ reset = false, nextSkip = 0 } = {}) => {
      const requestId = ++requestIdRef.current;

      setLoadingList(true);
      setPageError("");

      try {
        const cleanQuery = query.trim();
        const endpoint = cleanQuery
          ? "/listSuratMasukCompany"
          : "/listSuratMasukCompanyEmpty";

        const params = {
          skip: nextSkip,
          limit: LIMIT,
          status: statusnya,
          lokasi: address1,
          khusus,
          SearchThis: searchThis,
          query: cleanQuery,
        };

        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}${endpoint}`,
          { params }
        );

        if (requestId !== requestIdRef.current) return;

        const rows = normalizeResponseToArray(response.data);

        setPosts((prev) => (reset ? rows : [...prev, ...rows]));
        setHasMore(rows.length >= LIMIT);

        if (rows.length === 0) {
          setIsEnd("Selesai...");
        } else if (rows.length < LIMIT) {
          setIsEnd("Selesai...");
        } else {
          setIsEnd("");
        }
      } catch (error) {
        console.error(error);

        if (requestId !== requestIdRef.current) return;

        setPageError(
          error.response?.data?.message ||
            error.message ||
            "Gagal mengambil data surat masuk."
        );
        setIsEnd("Gagal load data.");
      } finally {
        if (requestId === requestIdRef.current) {
          setLoadingList(false);
        }
      }
    },
    [query, searchThis, address1, statusnya, khusus]
  );

  useEffect(() => {
    getCategorias();
  }, [getCategorias]);

  useEffect(() => {
    setSkip(0);
    fetchPosts({ reset: true, nextSkip: 0 });
  }, [fetchPosts]);

  const handleSearch = (event) => {
    setQuery(event.target.value);
    setSkip(0);
  };

  const handleChangeAddress = (event) => {
    setAddress1(event.target.value);
    setSkip(0);
  };

  const handleChangeStatus = (event) => {
    setStatusnya(event.target.value);
    setSkip(0);
  };

  const handleChangeKhusus = (event) => {
    setKhusus(event.target.value);
    setSkip(0);
  };

  const changeSearchThis = (event) => {
    setSearchThis(event.target.value);
    setSkip(0);
  };

  const handleScroll = (e) => {
    const { offsetHeight, scrollTop, scrollHeight } = e.target;

    if (loadingList || !hasMore) return;

    if (offsetHeight + scrollTop >= scrollHeight - 80) {
      const nextSkip = posts.length;
      setSkip(nextSkip);
      fetchPosts({ reset: false, nextSkip });
    }
  };

  const newMail = (post) => {
  const kontrakList = Array.isArray(post?.kontrak)
    ? post.kontrak
    : post?.kontrak
      ? [post.kontrak]
      : [];

  setUseData({
    ...(post || {}),
    kontrak: kontrakList[0] || null,
    kontrakList: kontrakList,
  });

  setOpenNewMail(true);
};

  const closeNewMail = () => {
    setUseData(null);
    setOpenNewMail(false);
  };

  const handleMailSaved = () => {
    setSkip(0);
    fetchPosts({ reset: true, nextSkip: 0 });
  };

  return (
    <Grid container>
      <Dialog
        open={openNewMail}
        onClose={(event, reason) => {
          /**
           * Prevent accidental close while admin is filling / submitting.
           * They can close with the button inside NewMailDialog.
           */
          if (reason === "backdropClick") return;
          closeNewMail();
        }}
        maxWidth="md"
        fullWidth
      >
        <NewMailDialog
          useData={useData}
          searchThis={searchThis}
          closeNewMail={closeNewMail}
          onSaved={handleMailSaved}
        />
      </Dialog>

      <Grid item xl={0.5} lg={0.7} md={0.9} sm={3.2} className="leftmenucolor">
        <LeftMenu />
      </Grid>

      <Grid item xl={10.8} lg={10.6} md={10.4} sm={8.8}>
        <Grid
          className="boxshadowclass"
          position="fixed"
          width="98%"
          xs={12}
          zIndex="100"
          opacity="1"
        >
          <Header
            judul="Surat/Paket Baru"
            isi="Halaman untuk menambahkan Surat/Paket yang baru datang"
          />
        </Grid>

        <Grid sx={{ mt: 15 }} />

        <Grid sx={{ pl: 15 }}>
          <Grid container xs={12} sx={{ mb: 6 }}>
            <Grid item xs={4}>
              <Typography>
                <b>Status:</b>
              </Typography>

              <FormControl component="fieldset">
                <RadioGroup value={statusnya} onChange={handleChangeStatus}>
                  <FormControlLabel
                    value="Aktif"
                    control={<Radio />}
                    label="Aktif"
                  />
                  <FormControlLabel
                    value="Non-Aktif"
                    control={<Radio />}
                    label="Expired"
                  />
                  <FormControlLabel
                    value="Semua"
                    control={<Radio />}
                    label="Semua"
                  />
                  <FormControlLabel
                    value="Belum-Mulai"
                    control={<Radio />}
                    label="Belum Mulai"
                  />
                </RadioGroup>
              </FormControl>
            </Grid>

            <Grid item xs={4}>
              <Typography>
                <b>Lokasi:</b>
              </Typography>

              <Select
                value={address1}
                whiteSpace="normal"
                onChange={handleChangeAddress}
                sx={{ minWidth: 260 }}
              >
                {categorias.map((categoria) => (
                  <MenuItem key={categoria} multiline value={categoria}>
                    {categoria}
                  </MenuItem>
                ))}

                <MenuItem multiline value="semua">
                  Semua Lokasi
                </MenuItem>
              </Select>
            </Grid>

            <Grid item xs={4}>
              <Typography>
                <b>Tipe Kontrak:</b>
              </Typography>

              <FormControl component="fieldset">
                <RadioGroup value={khusus} onChange={handleChangeKhusus}>
                  <FormControlLabel
                    value=""
                    control={<Radio />}
                    label="Semua"
                  />
                  <FormControlLabel
                    value="Virtual Office"
                    control={<Radio />}
                    label="Virtual Office Only"
                  />
                  <FormControlLabel
                    value="Private Office"
                    control={<Radio />}
                    label="Private Office Only"
                  />
                </RadioGroup>
              </FormControl>
            </Grid>
          </Grid>

          <Grid container sx={{ mb: 2 }} spacing={2}>
            <Grid item xs={2}>
              <Select fullWidth value={searchThis} onChange={changeSearchThis}>
                <MenuItem value="Perusahaan">Nama PT</MenuItem>
                <MenuItem value="Klien">Nama Orang</MenuItem>
              </Select>
            </Grid>

            <Grid item xs={5}>
              <TextField
                fullWidth
                value={query}
                onChange={handleSearch}
                placeholder={
                  searchThis === "Perusahaan"
                    ? "Cari nama PT..."
                    : "Cari nama klien..."
                }
              />
            </Grid>
          </Grid>

          {pageError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {pageError}
            </Alert>
          )}

          <Grid container alignItems="center" justifyContent="center">
            <Paper sx={{ width: "100%", overflow: "hidden" }}>
              {loadingList && (
                <Box sx={{ width: "100%" }}>
                  <LinearProgress />
                </Box>
              )}

              <TableContainer
                sx={{ maxHeight: 500, minHeight: 440 }}
                className="min-h-screen h-screen overflow-scroll"
                onScroll={handleScroll}
              >
                <Table stickyHeader aria-label="sticky table">
                  <TableHead>
                    <TableRow>
                      <TableCell>Nama Perusahaan</TableCell>
                      <TableCell>Nama Klien</TableCell>
                      <TableCell>Tipe Kontrak</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Keterangan</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {posts?.map((post, index) => {
                      const contract = getMainContract(post);
                      const companyName = getCompanyName(post);
                      const clientName = getClientName(post);
                      const blockReason = getRecipientBlockReason(post);

                      const status = getContractStatus(
                        contract?.TanggalMulai,
                        contract?.TanggalBerakhir,
                        contract?.termintateStat
                      );

                      const key =
                        contract?._id ||
                        `${post?.company?._id || "company"}-${
                          post?.klien?._id || "klien"
                        }-${index}`;

                      return (
                        <TableRow
                          key={key}
                          hover
                          onClick={() => newMail(post)}
                          sx={{
                            cursor: "pointer",
                            opacity: blockReason ? 0.72 : 1,
                          }}
                        >
                          <TableCell>
                            <p>{companyName || "-"}</p>
                          </TableCell>

                          <TableCell>
                            <p>
                              {clientName || (
                                <span style={{ color: "red" }}>
                                  KLIEN BELUM TERHUBUNG
                                </span>
                              )}
                            </p>

                            {getClientPhone(post) && (
                              <small>{getClientPhone(post)}</small>
                            )}
                          </TableCell>

                          <TableCell>
                            <p>{contract?.TipeKontrak || "-"}</p>
                          </TableCell>

                          <TableCell>
                            <Chip
                              size="small"
                              label={status}
                              color={getStatusColor(status)}
                            />
                          </TableCell>

                          <TableCell>
                            {blockReason ? (
                              <Alert severity="warning" sx={{ py: 0 }}>
                                {blockReason}
                              </Alert>
                            ) : (
                              <Chip
                                size="small"
                                color="success"
                                label="Siap kirim"
                              />
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}

                    {!loadingList && posts.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5}>
                          <Typography align="center" sx={{ py: 4 }}>
                            Tidak ada data.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}

                    {isEnd && posts.length > 0 && (
                      <TableRow>
                        <TableCell colSpan={5}>
                          <Typography align="center" sx={{ py: 2 }}>
                            {isEnd}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
}